import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ErrorService } from '../../error';
import { environment } from '../../../../environments/environment';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SalesTeamModel } from './types';
import { SalesTeamMember } from '../sales-team-member/types';
import { CrudType } from '../../service/message/types';
import { MessageService } from '../../service/message/message.service';
import { ListItem } from '../typeahead/types';

@Injectable({
  providedIn: 'root',
})
export class SalesTeamApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly errorService: ErrorService,
    private readonly messageService: MessageService,
  ) {}

  getById = (id: number): Observable<SalesTeamModel> => {
    let parameters: string[];
    parameters = [id.toString()];
    let url = this.formatUrl(parameters);
    return this.httpClient.get<SalesTeamModel>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };
  getList = (id: number): Observable<SalesTeamModel[]> => {
    let parameters: string[];
    parameters = ['list'];
    let url = this.formatUrl(parameters);
    return this.httpClient.get<SalesTeamModel[]>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };
  getCompanyCommissionList = (id: number): Observable<SalesTeamModel[]> => {
    let parameters: string[];
    parameters = ['list', 'companycommission'];
    let url = this.formatUrl(parameters);
    return this.httpClient.get<SalesTeamModel[]>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  getSalesTeamMemberList = (
    salesTeamIds: number[],
  ): Observable<SalesTeamMember[]> => {
    let parameters: string[];
    parameters = ['userlist'];
    let headerParams = new HttpHeaders();

    for (let i = 0; i < salesTeamIds.length; i++) {
      headerParams = headerParams.append(
        'salesTeamIds' + [],
        salesTeamIds[i].toString(),
      );
    }

    let url = this.formatUrl(parameters);
    return this.httpClient
      .get<SalesTeamMember[]>(url, { headers: headerParams })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getCompanyCommissionSalesTeamMemberList = (
    salesTeamIds: number[],
  ): Observable<SalesTeamMember[]> => {
    let parameters: string[];
    parameters = ['userlist', 'companycommission'];
    let headerParams = new HttpHeaders();

    for (let i = 0; i < salesTeamIds.length; i++) {
      headerParams = headerParams.append(
        'salesTeamIds' + [],
        salesTeamIds[i].toString(),
      );
    }
    let url = this.formatUrl(parameters);
    return this.httpClient
      .get<SalesTeamMember[]>(url, { headers: headerParams })
      .pipe(
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  getActiveSalesTeamsSelector = (
    salesTeamMemberIds: (number | null)[],
  ): Observable<ListItem[]> => {
    let parameters: string[];
    parameters = ['selector', 'salesteams'];

    // FromQuery côté API : chaque id est ajouté en paramètre de requête répété
    // (salesTeamMemberIds=1&salesTeamMemberIds=2&...). Le "non renseigné" part
    // en valeur vide (salesTeamMemberIds=) → bindé null côté API.
    let params = new HttpParams();
    for (let i = 0; i < salesTeamMemberIds.length; i++) {
      const id = salesTeamMemberIds[i];
      params = params.append(
        'salesTeamMemberIds',
        id == null ? '' : id.toString(),
      );
    }

    let url = this.formatUrl(parameters);
    return this.httpClient.get<ListItem[]>(url, { params }).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  getActiveSalesTeamMembersSelector = (
    salesTeamIds: (number | null)[],
  ): Observable<ListItem[]> => {
    let parameters: string[];
    parameters = ['selector', 'salesteammembers'];

    // FromQuery côté API : chaque id est ajouté en paramètre de requête répété
    // (salesTeamIds=1&salesTeamIds=2&...). Le "non renseigné" part en valeur
    // vide (salesTeamIds=) → bindé null côté API.
    let params = new HttpParams();
    for (let i = 0; i < salesTeamIds.length; i++) {
      const id = salesTeamIds[i];
      params = params.append('salesTeamIds', id == null ? '' : id.toString());
    }

    let url = this.formatUrl(parameters);
    return this.httpClient.get<ListItem[]>(url, { params }).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  create = (): Observable<SalesTeamModel> => {
    let parameters: string[];
    parameters = ['create'];
    let url = this.formatUrl(parameters);
    return this.httpClient.get<SalesTeamModel>(url).pipe(
      catchError((error) => {
        throw this.errorService.serverErrorRedirect(error);
      }),
    );
  };

  postSalesTeam = (salesTeam: SalesTeamModel): any => {
    let parameters: string[];
    parameters = [];
    let url = this.formatUrl(parameters);
    return this.httpClient
      .post<SalesTeamModel>(url, salesTeam, { observe: 'response' })
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.create),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  updateSalesTeam = (salesTeam: SalesTeamModel): any => {
    let parameters: string[];
    parameters = [salesTeam.id.toString()];
    let url = this.formatUrl(parameters);
    return this.httpClient
      .put<SalesTeamModel>(url, salesTeam, { observe: 'response' })
      .pipe(
        this.messageService.checkStatusCustomOperator(CrudType.update),
        catchError((error) => {
          throw this.errorService.serverErrorRedirect(error);
        }),
      );
  };

  formatUrl = (parameters: string[]): string => {
    var url = environment.comparanooapiurl;
    url += environment.features.salesteam.url;
    parameters.forEach((parameters) => {
      url += '/';
      url += parameters;
    });
    return url;
  };
}
